<template>
  <panel-layout
    id="courseRun-panel"
    title="Create new course"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <o-scroll v-if="!loading" class="h-full px-6 py-6">
      <o-steps ref="steps" :progress="progress" class="flex flex-col">
        <o-step v-slot="{ isActive }" :name="$t('pages.course.courseType')" :info="$t('pages.course.selectCourseType')" :success="isCompleteCourseType">
          <div
            :class="[
              'border rounded-lg dark:border-gray-800 p-4 bg-gray-50 dark:bg-gray-900',
              { 'ring-offset-2 ring-primary-200 ring-4 dark:ring-primary-800 dark:ring-offset-gray-900' : isActive }
            ]"
          >
            <o-field-group  v-if="!createNewCourse" :label="$t('pages.course.courseType')" class="block">
              <div>
                <CourseSelect v-model="course" :options="courses" />

                <p class="text-right mt-1">
                  <a
                    class="text-blue-500 text-sm cursor-pointer hover:underline text-right"
                    @click="createNewCourse = true"
                  >
                    {{ $t('pages.course.createNewCourse') }}
                  </a>
                </p>
              </div>
            </o-field-group>

            <div v-else class="space-y-4">
              <o-field-group :label="$t('general.title')" class="mb-4 block">
                <text-field
                  v-model="newCourse.name"
                  :label="$t('general.name')"
                  :placeholder="$t('pages.course.enterCourseNamePlaceholder')"
                  :disabled="!!newCourseModel"
                  required
                />
              </o-field-group>

              <o-field-group :label="$t('general.information')" class="mb-4 block">
                <text-area-field
                  v-model="newCourse.information"
                  :label="$t('general.information')"
                  :placeholder="$t('pages.course.enterCourseInfoPlaceholder')"
                  :disabled="!!newCourseModel"
                  required
                />
              </o-field-group>

              <div v-if="!newCourseModel" class="flex items-center justify-between">
                <a
                  class="text-blue-500 text-sm cursor-pointer hover:underline"
                  @click="createNewCourse = false"
                >
                  {{ $t('pages.course.selectExistingCourse') }}
                </a>

                <o-button
                  variant="primary"
                  size="sm"
                  :loading="creatingCourse"
                  :disabled="!newCourse.name || !newCourse.information || creatingCourse"
                  @click="createNewCourseModel"
                >
                  {{ $t('pages.course.createCourseContinue') }}
                </o-button>
              </div>
            </div>
          </div>
        </o-step>

        <o-step v-slot="{ isActive }" :name="$t('general.details')" :info="$t('pages.course.create.enterDetails')" :success="isCompleteDetails">
          <div
            :class="[
              'border rounded-lg dark:border-gray-800 p-4 bg-gray-50 dark:bg-gray-900',
              { 'ring-offset-2 ring-primary-200 ring-4 dark:ring-primary-800 dark:ring-offset-gray-900' : isActive }
            ]"
          >
            <o-field-group v-if="courseRun" :label="$t('general.name')" :info="$t('pages.course.labelForCourseRunInfo')" class="mb-4 block">
              <text-field
                v-model="courseRun.information"
                :label="$t('general.name')"
                :placeholder="$t('pages.course.courseLabelPlaceholder')"
                required
              />
            </o-field-group>

            <p v-else class="text-sm text-gray-500 dark:text-gray-400">
              {{ $t('pages.course.selectCourseTypeFirst') }}
            </p>
          </div>
        </o-step>

        <o-step v-slot="{ isActive }" :name="$t('pages.course.pricing')" :info="$t('pages.course.setPricingDetails')" :success="isCompletePricing">
          <div
            :class="[
              'border rounded-lg dark:border-gray-800 p-4 bg-gray-50 dark:bg-gray-900',
              { 'ring-offset-2 ring-primary-200 ring-4 dark:ring-primary-800 dark:ring-offset-gray-900' : isActive }
            ]"
          >
            <pricing-schemes v-if="courseRun" :payment-config="courseRun.paymentConfig" />

            <p v-else class="text-sm text-gray-500 dark:text-gray-400">
              {{ $t('pages.course.selectCourseTypeFirst') }}
            </p>
          </div>
        </o-step>
      </o-steps>
    </o-scroll>

    <template #footer>
      <div v-if="!loading">
        <div class="flex justify-between align-items">
          <div class="flex items-center space-x-1">
            <o-button outline flat class="mr-2" @click="cancel()">
              {{ $t('general.cancel') }}
            </o-button>
          </div>
          <div class="flex items-center space-x-1">
            <o-button
              variant="primary"
              class="mr-2"
              :disabled="!isComplete"
              @click="save()"
            >
              {{ $t('general.continue') }}
            </o-button>
          </div>
        </div>
      </div>
    </template>

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      icon="courseRun"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        {
          text: $t('general.closeWithoutSaving'),
          type: 'confirm',
          variant: undefined,
          flat: true,
          outline: true,
          callback: () => cancel(true)
        },
        {
          text: $t('general.saveAndClose'),
          type: 'confirm',
          response: 'save',
          callback: () => save(true)
        }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">

// @ts-ignore
import Session from '@/components/courseRun/elements/Session.vue'
// @ts-ignore
import CourseSelect from '@/components/course/elements/Select.vue'
// @ts-ignore
import PricingSchemes from '@/components/courseRun/elements/PricingSchemes.vue'
// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'

import { OTabs, OTab, OAlert, OAvatar, OSteps, OStep } from '@/components/elements'
import { TextField, TextAreaField, DateField, CurrencyField, ToggleField, NumberField, SegmentField } from '@/components/fields'


import { usePanel } from '~/composables/usePanel'

const panel = usePanel()

const props = defineProps<{ id?: string }>()

const { $api, $notification, $bus } = useNuxtApp()

const course = ref<any>(null)
const courses = ref<any[]>([])
const courseRun = ref<any>(null)
const errors = ref(false)
const loading = ref(true)
const creatingCourse = ref(false)
const newCourseModel = ref<any>(null)
const createNewCourse = ref(false)
const newCourse = ref({ name: '', information: '' })

const confirmDialog = ref<any>()
const steps = ref<any>()

const { unsavedChanges, snapshot } = useUnsavedChanges(courseRun)

const isCompleteCourseType = computed(() => {
  return !!courseRun.value
})

const isCompleteDetails = computed(() => {
  return !!courseRun.value?.information && courseRun.value.information.trim() !== ''
})

const isCompletePricing = computed(() => {
  const paymentConfig = courseRun.value?.paymentConfig
  if (!paymentConfig) {
    return false
  }
  if (paymentConfig.schemes?.length) {
    return paymentConfig.schemes.every((scheme: any) => !!scheme.name && scheme.price !== null && scheme.price !== undefined && scheme.price !== '')
  }
  return !!paymentConfig.price
})

const isComplete = computed(() => {
  return isCompleteCourseType.value && isCompleteDetails.value && isCompletePricing.value
})

const progress = computed(() => {
  if (!isCompleteCourseType.value) return 1
  if (!isCompleteDetails.value) return 2
  if (!isCompletePricing.value) return 3
  return 4
})

watch(course, async (value) => {
  if (createNewCourse.value) {
    return
  }
  if (!value) {
    courseRun.value = null
    return
  }
  const idCourse = value.idCourse ?? value.id
  if (idCourse != null) {
    await fetchCourseRun(idCourse)
  }
}, { deep: true })

watch(createNewCourse, () => {
  course.value = null
  courseRun.value = null
  newCourseModel.value = null
})

const fetchCourses = async () => {
  try {
    const response = await $api.course.list()
    courses.value = response.items

    if (props.id) {
      const idCourse = Number(props.id)
      course.value = courses.value.find(item => (item.idCourse ?? item.id) === idCourse) || null
    }
  } catch (e) {
    console.log(e)
  }
}

const fetchCourseRun = async (idCourse?: number | string) => {
  try {
    const response = await $api.course.getRunDefaults(idCourse)
    courseRun.value = response.data
    delete courseRun.value.reservations
    courseRun.value.paymentConfig.earlyOffset = null
    courseRun.value.paymentConfig.earlyPrice = null
    snapshot()
  } catch (e) {
    console.log(e)
  }
}

const createNewCourseModel = async () => {
  creatingCourse.value = true

  try {
    const newCourseObj = (await $api.course.defaults({} as any)).data
    newCourseObj.name = newCourse.value.name
    newCourseObj.information = newCourse.value.information
    const savedCourse = (await $api.course.save(newCourseObj)).data

    if (savedCourse) {
      newCourseModel.value = savedCourse
      await fetchCourseRun(savedCourse.idCourse)
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  } finally {
    creatingCourse.value = false
  }
}

const save = async (close = false) => {
  loading.value = true
  errors.value = false

  if (!courseRun.value?.information || courseRun.value.information.trim() === '') {
    $notification.error({ message: 'Label is required. Please enter a label for this course run.' })
    loading.value = false
    return false
  }

  try {
    let run = courseRun.value

    if (createNewCourse.value && !newCourseModel.value) {
      await createNewCourseModel()
      run = courseRun.value
    }

    const response = await $api.course.saveRun(run)
    const result = response.data

    if (result) {
      courseRun.value = result
      // The run is now persisted — reset the baseline so the unsaved-changes
      // guard doesn't block closing/navigating away.
      snapshot()
      $bus.emit.updated('CourseRun', result)

      if (close) {
        panel.close(true)
      } else {
        // Progress to the run edit screen for the newly created run.
        panel.replace('course/edit', { idCourseRun: String(result.idCourseRun) })
      }
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
    loading.value = false
    return false
  }

  loading.value = false
}

const cancel = (force = false) => {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

const handleEnableEarlyBird = () => {
  const price = courseRun.value.paymentConfig.price
  courseRun.value.paymentConfig.earlyOffset = 10
  courseRun.value.paymentConfig.earlyPrice = price ? price - (price * 0.2) : 0
}

const handleDisableEarlyBird = () => {
  courseRun.value.paymentConfig.earlyOffset = null
  courseRun.value.paymentConfig.earlyPrice = null
}

const handleBreakAddClick = () => {
  courseRun.value.paymentConfig.instalments.push({ amount: 0.5, offset: 30 })
}

const handleAddInstalment = () => {
  courseRun.value.paymentConfig.instalments.push({ amount: 0.5, offset: 30 })
}

const handleRemoveInstalment = (index: number) => {
  courseRun.value.paymentConfig.instalments.splice(index, 1)
}

// created
;(async () => {
  await fetchCourses()

  if (!courses.value.length) {
    createNewCourse.value = true
  }

  loading.value = false
})()
</script>
